import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

export interface FiltrPolozka {
  id: string;
  label: string;
}

interface FiltrVyberProps {
  /** Popisek před dvojtečkou, např. „Značky". */
  nazev: string;
  polozky: FiltrPolozka[];
  /** Vybraná id; prázdné pole = „Vše". */
  vybrane: string[];
  onZmena: (vybrane: string[]) => void;
  /**
   * Jen jedna volba naráz (výběr nahradí předchozí). Pro filtry typu „Typ",
   * kde se možnosti vylučují.
   */
  jenJedna?: boolean;
  /** Kolik vybraných se vypíše do tlačítka, než se zbytek zkrátí na „+N". */
  maxVypsanych?: number;
}

/**
 * Filtrační tlačítko s rozbalovací nabídkou a zaškrtáváním. Navazuje na nízké
 * přepínače u promo obrázků, ale voleb je víc, než se vejde do řady – proto
 * rozbalovací nabídka. Vybrané se vypisují do popisku tlačítka
 * („Značky: Mora, Beko"), aby bylo i po zavření vidět, čím je seznam omezený.
 */
export const FiltrVyber: React.FC<FiltrVyberProps> = ({
  nazev,
  polozky,
  vybrane,
  onZmena,
  jenJedna = false,
  maxVypsanych = 2,
}) => {
  const [otevreno, setOtevreno] = useState(false);
  const obal = useRef<HTMLDivElement>(null);

  // Zavřít kliknutím mimo nebo Escapem – nabídka překrývá tabulku pod sebou.
  useEffect(() => {
    if (!otevreno) return;

    const klik = (e: MouseEvent) => {
      if (obal.current && !obal.current.contains(e.target as Node)) setOtevreno(false);
    };
    const klavesa = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOtevreno(false);
    };

    document.addEventListener('mousedown', klik);
    document.addEventListener('keydown', klavesa);
    return () => {
      document.removeEventListener('mousedown', klik);
      document.removeEventListener('keydown', klavesa);
    };
  }, [otevreno]);

  const prepni = (id: string) => {
    if (jenJedna) {
      onZmena(vybrane.includes(id) ? [] : [id]);
      setOtevreno(false);
      return;
    }
    onZmena(vybrane.includes(id) ? vybrane.filter((x) => x !== id) : [...vybrane, id]);
  };

  const popisky = vybrane
    .map((id) => polozky.find((p) => p.id === id)?.label)
    .filter(Boolean) as string[];

  const popisek = () => {
    if (!popisky.length) return 'Vše';
    if (popisky.length <= maxVypsanych) return popisky.join(', ');
    return `${popisky.slice(0, maxVypsanych).join(', ')} +${popisky.length - maxVypsanych}`;
  };

  const aktivni = vybrane.length > 0;

  return (
    <div className="relative" ref={obal}>
      <button
        type="button"
        onClick={() => setOtevreno((o) => !o)}
        aria-expanded={otevreno}
        aria-haspopup="true"
        title={aktivni ? `${nazev}: ${popisky.join(', ')}` : `${nazev} – bez filtru`}
        className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium transition-colors max-w-[18rem] ${
          aktivni
            ? 'bg-blue-600 border-blue-600 text-white'
            : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
        }`}
      >
        <span className="truncate">
          {nazev}: {popisek()}
        </span>
        <ChevronDown className="w-3 h-3 flex-shrink-0" />
      </button>

      {otevreno && (
        <div className="absolute right-0 z-20 mt-1 w-56 max-h-72 overflow-y-auto overscroll-contain rounded-lg border-2 border-gray-400 bg-white shadow-xl">
          <div className="sticky top-0 border-b-2 border-gray-300 bg-gray-100 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-600">
            {nazev}
          </div>

          <button
            type="button"
            onClick={() => {
              onZmena([]);
              setOtevreno(false);
            }}
            className="mt-1 flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100"
          >
            <span className="w-4 flex-shrink-0">
              {!aktivni && <Check className="w-4 h-4 text-blue-600" />}
            </span>
            Vše
          </button>

          <div className="my-1 border-t border-gray-200" />

          {polozky.map((p) => {
            const zaskrtnuto = vybrane.includes(p.id);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => prepni(p.id)}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-gray-100 ${
                  zaskrtnuto ? 'bg-blue-50 font-medium text-gray-900' : 'text-gray-700'
                }`}
              >
                <span className="w-4 flex-shrink-0">
                  {zaskrtnuto && <Check className="w-4 h-4 text-blue-600" />}
                </span>
                <span className="truncate" title={p.label}>
                  {p.label}
                </span>
              </button>
            );
          })}
          <div className="h-1" />
        </div>
      )}
    </div>
  );
};
